import { useQuery } from '@tanstack/react-query';
import {
  ActionStack,
  Alert,
  Badge,
  Button,
  Card,
  DataList,
  Dialog,
  PageTitle,
  pillClass,
  SelectField,
  TextField,
  TEXT_LINK,
} from '@rooming/ui';
import { formatDateRangeInZone } from '@rooming/web-core';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { HotelAdminsSection } from './HotelAdminsSection';
import {
  backoffice,
  type AccessEvent,
  type AuditEvent,
  type ExternalUser,
  type Group,
  type HistoryEntry,
  type Hotel,
  type StaffRole,
  type StaffUser,
  type Stay,
} from '../api';
import { Loaded, PasswordResetDialog, ReasonDialog, STAY_STATUS, useCommand } from '../common';
import { useSession } from '../session';

// The one internal role is the administrator. The others are legacy (no permission): shown only so an administrator
// can see and remove them from an old account.
const ROLE_TEXT: Record<StaffRole, string> = {
  TENANT_ADMIN: 'Super administrador',
  OPERATOR: 'Operador (sin uso)',
  SUPPORT: 'Soporte (sin uso)',
  AUDITOR: 'Auditor (sin uso)',
  HOTEL_ADMIN: 'Administrador de hotel',
};

// ── Dashboard: what needs attention today (called "Dashboard" on screen, owner 2026-10-07) ──

export function DashboardPage() {
  const navigate = useNavigate();
  const stays = useQuery({ queryKey: ['stays', 'all'], queryFn: () => backoffice.stays() });
  const groups = useQuery({ queryKey: ['groups'], queryFn: backoffice.groups });
  const hotels = useQuery({ queryKey: ['hotels'], queryFn: backoffice.hotels });
  // D-3: the progress of every stay in use comes in ONE request (not one per stay).
  const progress = useQuery({ queryKey: ['distribution', 'all'], queryFn: backoffice.progress });
  const progressOf = (stayId: string) => progress.data?.items.find((p) => p.stayId === stayId);
  return (
    <>
      <PageTitle subtitle="Las estadías y su avance. Tocá una fila para abrirla.">Dashboard</PageTitle>
      <Loaded query={stays}>
        {(items) => {
          const open = items.filter((s) => s.status === 'OPEN' || s.status === 'REOPENED' || s.status === 'CONFIRMED');
          const draft = items.filter((s) => s.status === 'DRAFT');
          return (
            <div className="flex flex-col gap-6">
              <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <Stat label="Estadías" value={items.length} />
                <Stat label="En curso" value={open.length} />
                <Stat label="En preparación" value={draft.length} />
                <Stat label="Grupos" value={groups.data?.length ?? 0} />
              </dl>
              <section className="flex flex-col gap-3">
                <h2 className="text-xl font-semibold">En curso</h2>
                <DataList<Stay>
                  caption="Estadías en curso"
                  rows={open}
                  rowKey={(s) => s.id}
                  onRowClick={(s) => void navigate(`/grupos/${s.groupId}/distribucion?estadia=${s.id}`)}
                  empty="No hay estadías con la selección abierta o confirmada."
                  columns={[
                    { header: 'Grupo', cell: (s) => groups.data?.find((g) => g.id === s.groupId)?.name ?? '—' },
                    { header: 'Hotel', cell: (s) => hotels.data?.find((h) => h.id === s.hotelId)?.name ?? '—' },
                    {
                      header: 'Fechas (hora del hotel)',
                      cell: (s) =>
                        formatDateRangeInZone(
                          s.checkInAt,
                          s.checkOutAt,
                          hotels.data?.find((h) => h.id === s.hotelId)?.timezone ?? 'UTC',
                        ),
                    },
                    {
                      header: 'Estado',
                      cell: (s) => (
                        <Badge tone={STAY_STATUS[s.status]?.tone ?? 'neutral'}>
                          {STAY_STATUS[s.status]?.text ?? s.status}
                        </Badge>
                      ),
                    },
                    {
                      header: 'Avance',
                      cell: (s) => {
                        const p = progressOf(s.id);
                        return p === undefined
                          ? '…'
                          : `${String(p.assigned)}/${String(p.passengers)} (${String(p.percentAssigned)}%)`;
                      },
                    },
                    {
                      header: 'Distribución',
                      end: true,
                      cell: (s) => (
                        <Link to={`/grupos/${s.groupId}/distribucion?estadia=${s.id}`} className={TEXT_LINK}>
                          Ver
                        </Link>
                      ),
                    },
                  ]}
                />
              </section>
              <section className="flex flex-col gap-3">
                <h2 className="text-xl font-semibold">En preparación</h2>
                <DataList<Stay>
                  caption="Estadías en preparación"
                  rows={draft}
                  rowKey={(s) => s.id}
                  onRowClick={(s) => void navigate(`/grupos/${s.groupId}/resumen?estadia=${s.id}`)}
                  empty="No hay estadías en preparación."
                  columns={[
                    { header: 'Grupo', cell: (s) => groups.data?.find((g) => g.id === s.groupId)?.name ?? '—' },
                    { header: 'Hotel', cell: (s) => hotels.data?.find((h) => h.id === s.hotelId)?.name ?? '—' },
                    { header: 'Estado', cell: (s) => <Badge>{STAY_STATUS[s.status]?.text ?? s.status}</Badge> },
                    {
                      header: 'Ficha',
                      end: true,
                      cell: (s) => (
                        <Link to={`/grupos/${s.groupId}/resumen?estadia=${s.id}`} className={TEXT_LINK}>
                          Abrir
                        </Link>
                      ),
                    },
                  ]}
                />
              </section>
            </div>
          );
        }}
      </Loaded>
    </>
  );
}

function Stat({ label, value }: { readonly label: string; readonly value: number }) {
  return (
    <div className="rounded-xl border border-line bg-surface p-4 shadow-sm">
      <dt className="text-sm text-muted">{label}</dt>
      <dd className="text-3xl font-semibold">{value}</dd>
    </div>
  );
}

// ── Usuarios y permisos (TENANT_ADMIN) ──

export function UsersPage() {
  const staff = useQuery({ queryKey: ['staff-users'], queryFn: async () => (await backoffice.staffUsers()).items });
  const externals = useQuery({
    queryKey: ['external-users'],
    queryFn: async () => (await backoffice.externalUsers()).items,
  });
  const groups = useQuery({ queryKey: ['groups'], queryFn: backoffice.groups });
  const stays = useQuery({ queryKey: ['stays', 'all'], queryFn: () => backoffice.stays() });
  const hotels = useQuery({ queryKey: ['hotels'], queryFn: backoffice.hotels });
  const { busy, run } = useCommand();
  const emptyForm = { email: '', displayName: '', initialPassword: '', type: '' as ExternalUser['type'] | '' };
  const [form, setForm] = useState(emptyForm);
  const [granting, setGranting] = useState<{ user: ExternalUser; role: 'HOTEL_USER' | 'COORDINATOR' }>();
  const [target, setTarget] = useState('');
  const [revoking, setRevoking] = useState<{ user: ExternalUser; grantId: string }>();
  const [toggling, setToggling] = useState<ExternalUser>();
  const [ending, setEnding] = useState<StaffUser>();
  const [resetting, setResetting] = useState<{ id: string; name: string; mustChange: boolean }>();
  const [resettingFactor, setResettingFactor] = useState<StaffUser>();
  const { me } = useSession();
  const [rolesOf, setRolesOf] = useState<string>();
  const editing = staff.data?.find((user) => user.id === rolesOf);

  const stayText = (stayId: string): string => {
    const stay = stays.data?.find((s) => s.id === stayId);
    if (stay === undefined) return 'Estadía';
    const group = groups.data?.find((g) => g.id === stay.groupId)?.name ?? 'Grupo';
    const hotel = hotels.data?.find((h) => h.id === stay.hotelId);
    return `${hotel?.name ?? 'Hotel'} · ${group} · ${formatDateRangeInZone(stay.checkInAt, stay.checkOutAt, hotel?.timezone ?? 'UTC')}`;
  };
  const groupText = (groupId: string): string => groups.data?.find((g) => g.id === groupId)?.name ?? 'Grupo';
  const grantText = (grant: ExternalUser['grants'][number]): string =>
    grant.role === 'HOTEL_USER' ? `Hotel: ${stayText(grant.stayId)}` : `Guía de: ${groupText(grant.groupId)}`;

  return (
    <>
      <PageTitle subtitle="Personal, usuarios de hotel y guías. Todo cambio queda en la auditoría.">
        Usuarios y permisos
      </PageTitle>
      <div className="flex flex-col gap-8">
        <section className="flex flex-col gap-3">
          <h2 className="text-xl font-semibold">Personal</h2>
          <Loaded query={staff}>
            {(items) => (
              <DataList<StaffUser>
                caption="Personal"
                rows={items}
                rowKey={(u) => u.id}
                empty="No hay usuarios de personal."
                columns={[
                  { header: 'Nombre', cell: (u) => u.displayName },
                  { header: 'Correo', cell: (u) => u.email },
                  {
                    header: 'Roles',
                    cell: (u) => (
                      <span className="flex flex-wrap gap-1">
                        {u.roles.length === 0
                          ? '—'
                          : u.roles.map((role) => (
                              <Badge key={role} tone="info">
                                {ROLE_TEXT[role]}
                              </Badge>
                            ))}
                      </span>
                    ),
                  },
                  {
                    header: 'Dos pasos',
                    cell: (u) => (
                      <Badge tone={u.mfaEnrolled ? 'success' : 'warning'}>
                        {u.mfaEnrolled ? 'Configurado' : 'Pendiente'}
                      </Badge>
                    ),
                  },
                  {
                    header: 'Acciones',
                    end: true,
                    cell: (u) => (
                      <ActionStack>
                        <Button
                          variant="primary"
                          size="sm"
                          aria-label={`Cambiar los roles de ${u.displayName}`}
                          onClick={() => {
                            setRolesOf(u.id);
                          }}
                        >
                          Roles
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          aria-label={`Cerrar las sesiones de ${u.displayName}`}
                          onClick={() => {
                            setEnding(u);
                          }}
                        >
                          Cerrar sesiones
                        </Button>
                        {u.id !== me?.id && (
                          <Button
                            variant="primary"
                            size="sm"
                            aria-label={`Restablecer la contraseña de ${u.displayName}`}
                            onClick={() => {
                              setResetting({ id: u.id, name: u.displayName, mustChange: false });
                            }}
                          >
                            Restablecer contraseña
                          </Button>
                        )}
                        {u.id !== me?.id && u.mfaEnrolled && (
                          <Button
                            variant="primary"
                            size="sm"
                            aria-label={`Reiniciar la verificación en dos pasos de ${u.displayName}`}
                            onClick={() => {
                              setResettingFactor(u);
                            }}
                          >
                            Reiniciar dos pasos
                          </Button>
                        )}
                      </ActionStack>
                    ),
                  },
                ]}
              />
            )}
          </Loaded>
          <p className="text-sm text-muted">
            El alta de personal y sus roles iniciales se hace por comando de operación (no hay registro público).
            Conviene que haya siempre al menos dos administradores: si uno pierde su segundo factor, lo reinicia el
            otro.
          </p>
        </section>

        <HotelAdminsSection />

        <section className="flex flex-col gap-3">
          <h2 className="text-xl font-semibold">Hoteles y guías</h2>
          <Loaded query={externals}>
            {(items) => (
              <DataList<ExternalUser>
                caption="Usuarios de hotel y guías"
                rows={items}
                rowKey={(u) => u.id}
                empty="Todavía no hay usuarios externos."
                columns={[
                  { header: 'Nombre', cell: (u) => u.displayName },
                  { header: 'Correo', cell: (u) => u.email },
                  { header: 'Tipo', cell: (u) => (u.type === 'HOTEL' ? 'Hotel' : 'Guía') },
                  {
                    header: 'Estado',
                    cell: (u) => (
                      <Badge tone={u.status === 'ACTIVE' ? 'success' : 'neutral'}>
                        {u.status === 'ACTIVE' ? 'Activo' : 'Deshabilitado'}
                      </Badge>
                    ),
                  },
                  {
                    header: 'Puede ver',
                    cell: (u) =>
                      u.grants.length === 0 ? (
                        'Nada todavía'
                      ) : (
                        <ul className="flex flex-col gap-1">
                          {u.grants.map((grant) => (
                            <li key={grant.id}>{grantText(grant)}</li>
                          ))}
                        </ul>
                      ),
                  },
                  {
                    header: 'Acciones',
                    end: true,
                    cell: (u) => (
                      <ActionStack>
                        {u.type === 'HOTEL' ? (
                          <Button
                            variant="primary"
                            size="sm"
                            aria-label={`Dar una estadía a ${u.displayName}`}
                            onClick={() => {
                              setTarget('');
                              setGranting({ user: u, role: 'HOTEL_USER' });
                            }}
                          >
                            Dar acceso a una estadía
                          </Button>
                        ) : (
                          <Button
                            variant="primary"
                            size="sm"
                            aria-label={`Dar un grupo a ${u.displayName}`}
                            onClick={() => {
                              setTarget('');
                              setGranting({ user: u, role: 'COORDINATOR' });
                            }}
                          >
                            Dar acceso a un grupo
                          </Button>
                        )}
                        {u.grants.length > 0 && (
                          <Button
                            variant="primary"
                            size="sm"
                            aria-label={`Quitar un acceso a ${u.displayName}`}
                            onClick={() => {
                              setRevoking({ user: u, grantId: u.grants[0]?.id ?? '' });
                            }}
                          >
                            Quitar acceso
                          </Button>
                        )}
                        <Button
                          variant="primary"
                          size="sm"
                          aria-label={`${u.status === 'ACTIVE' ? 'Deshabilitar' : 'Habilitar'} a ${u.displayName}`}
                          onClick={() => {
                            setToggling(u);
                          }}
                        >
                          {u.status === 'ACTIVE' ? 'Deshabilitar' : 'Habilitar'}
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          aria-label={`Restablecer la contraseña de ${u.displayName}`}
                          onClick={() => {
                            setResetting({ id: u.id, name: u.displayName, mustChange: true });
                          }}
                        >
                          Restablecer contraseña
                        </Button>
                      </ActionStack>
                    ),
                  },
                ]}
              />
            )}
          </Loaded>
          <Card>
            <h3 className="mb-3 text-lg font-semibold">Nuevo usuario externo</h3>
            <form
              className="grid gap-4 md:grid-cols-2"
              onSubmit={(event) => {
                event.preventDefault();
                const type = form.type;
                if (type === '') return;
                void run(
                  () => backoffice.createExternalUser({ ...form, type, email: form.email.trim() }),
                  'Usuario creado.',
                  [['external-users']],
                ).then((ok) => {
                  if (ok) setForm(emptyForm);
                });
              }}
            >
              <SelectField
                label="Tipo de usuario"
                placeholder="Elegí el tipo"
                hint="No se puede cambiar después. Hotel: ve las estadías de su hotel y carga los números de habitación. Guía: ve los grupos que acompaña. Ninguno usa verificación en dos pasos."
                options={[
                  { value: 'HOTEL', label: 'Hotel' },
                  { value: 'COORDINATOR', label: 'Guía' },
                ]}
                required
                value={form.type}
                onChange={(event) => {
                  setForm({ ...form, type: event.target.value as ExternalUser['type'] | '' });
                }}
              />
              <TextField
                label="Nombre"
                required
                value={form.displayName}
                onChange={(event) => {
                  setForm({ ...form, displayName: event.target.value });
                }}
              />
              <TextField
                label="Correo"
                type="email"
                autoComplete="off"
                required
                value={form.email}
                onChange={(event) => {
                  setForm({ ...form, email: event.target.value });
                }}
              />
              <TextField
                label="Contraseña inicial"
                type="password"
                autoComplete="new-password"
                hint="Al menos 12 caracteres. Entregala por un canal seguro: para un usuario de hotel es su única credencial."
                required
                value={form.initialPassword}
                onChange={(event) => {
                  setForm({ ...form, initialPassword: event.target.value });
                }}
              />
              <div className="md:col-span-2">
                <Button
                  type="submit"
                  loading={busy}
                  disabled={
                    form.type === '' ||
                    form.email.trim() === '' ||
                    form.displayName.trim() === '' ||
                    form.initialPassword.length < 12
                  }
                >
                  Crear usuario
                </Button>
              </div>
            </form>
          </Card>
        </section>
      </div>

      <Dialog
        open={editing !== undefined}
        title={`Roles de ${editing?.displayName ?? ''}`}
        onClose={() => {
          if (!busy) setRolesOf(undefined);
        }}
        actions={
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => {
              setRolesOf(undefined);
            }}
          >
            Cerrar
          </Button>
        }
      >
        <ul className="flex flex-col gap-3">
          {(Object.keys(ROLE_TEXT) as StaffRole[]).map((role) => {
            const has = editing?.roles.includes(role) === true;
            // Legacy roles are never granted: they appear only on an account that still holds one, to remove it.
            if (role !== 'TENANT_ADMIN' && !has) return null;
            return (
              <li key={role} className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  {ROLE_TEXT[role]} {has && <Badge tone="info">Asignado</Badge>}
                </span>
                {role === 'HOTEL_ADMIN' ? (
                  <span className="text-sm text-muted">Sus hoteles se cambian en «Administradores de hotel».</span>
                ) : (
                  <Button
                    variant={has ? 'secondary' : 'primary'}
                    disabled={busy}
                    aria-label={`${has ? 'Quitar' : 'Dar'} el rol ${ROLE_TEXT[role]}`}
                    onClick={() => {
                      if (editing === undefined) return;
                      void run(
                        () =>
                          has
                            ? backoffice.revokeRole(editing.id, role)
                            : backoffice.grantRole(editing.id, 'TENANT_ADMIN'),
                        has ? 'Rol quitado.' : 'Rol asignado.',
                        [['staff-users']],
                      );
                    }}
                  >
                    {has ? 'Quitar' : 'Dar'}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
        <p className="mt-3 text-sm text-muted">
          Sin un rol de administración, una cuenta de personal no puede hacer nada. Los cambios tienen efecto inmediato
          y quedan en la auditoría. Siempre queda al menos un super administrador. Los administradores de hotel se crean
          y se acotan a sus hoteles en su propia sección.
        </p>
      </Dialog>

      <ReasonDialog
        open={granting !== undefined}
        title={granting?.role === 'HOTEL_USER' ? 'Dar acceso a una estadía' : 'Dar acceso a un grupo'}
        confirmLabel="Dar acceso"
        busy={busy}
        disabled={target === ''}
        onConfirm={(reason) => {
          if (granting === undefined) return;
          const grant =
            granting.role === 'HOTEL_USER'
              ? ({ role: 'HOTEL_USER', stayId: target, reason } as const)
              : ({ role: 'COORDINATOR', groupId: target, reason } as const);
          void run(() => backoffice.grantExternal(granting.user.id, grant), 'Permiso otorgado.', [
            ['external-users'],
          ]).then((ok) => {
            if (ok) setGranting(undefined);
          });
        }}
        onClose={() => {
          setGranting(undefined);
        }}
      >
        {granting?.role === 'HOTEL_USER' ? (
          <SelectField
            label="Estadía"
            placeholder="Elegí una estadía"
            hint="Un usuario de hotel solo puede tener estadías de un mismo hotel."
            options={(stays.data ?? []).map((s) => ({ value: s.id, label: stayText(s.id) }))}
            value={target}
            onChange={(event) => {
              setTarget(event.target.value);
            }}
          />
        ) : (
          <SelectField
            label="Grupo"
            placeholder="Elegí un grupo"
            options={(groups.data ?? []).map((g) => ({ value: g.id, label: `${g.code} — ${g.name}` }))}
            value={target}
            onChange={(event) => {
              setTarget(event.target.value);
            }}
          />
        )}
      </ReasonDialog>

      <ReasonDialog
        open={revoking !== undefined}
        title="¿Quitar el permiso?"
        confirmLabel="Quitar"
        danger
        busy={busy}
        onConfirm={(reason) => {
          if (revoking === undefined) return;
          void run(() => backoffice.revokeExternal(revoking.user.id, revoking.grantId, reason), 'Permiso quitado.', [
            ['external-users'],
          ]).then((ok) => {
            if (ok) setRevoking(undefined);
          });
        }}
        onClose={() => {
          setRevoking(undefined);
        }}
      >
        <div className="flex flex-col gap-3">
          {revoking !== undefined && revoking.user.grants.length > 1 ? (
            <SelectField
              label="Acceso a quitar"
              options={revoking.user.grants.map((grant) => ({ value: grant.id, label: grantText(grant) }))}
              value={revoking.grantId}
              onChange={(event) => {
                setRevoking({ ...revoking, grantId: event.target.value });
              }}
            />
          ) : null}
          <p>
            {revoking?.user.displayName} deja de ver:{' '}
            {grantText(
              revoking?.user.grants.find((grant) => grant.id === revoking.grantId) ?? {
                id: '',
                role: 'COORDINATOR',
                groupId: '',
              },
            )}
            . Tiene efecto inmediato.
          </p>
        </div>
      </ReasonDialog>

      <ReasonDialog
        open={toggling !== undefined}
        title={toggling?.status === 'ACTIVE' ? '¿Deshabilitar la cuenta?' : '¿Habilitar la cuenta?'}
        confirmLabel={toggling?.status === 'ACTIVE' ? 'Deshabilitar' : 'Habilitar'}
        danger={toggling?.status === 'ACTIVE'}
        busy={busy}
        onConfirm={(reason) => {
          if (toggling === undefined) return;
          const next = toggling.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE';
          void run(() => backoffice.setExternalStatus(toggling.id, next, reason), 'Cuenta actualizada.', [
            ['external-users'],
          ]).then((ok) => {
            if (ok) setToggling(undefined);
          });
        }}
        onClose={() => {
          setToggling(undefined);
        }}
      >
        <p>
          {toggling?.status === 'ACTIVE'
            ? `${toggling.displayName} no podrá ingresar y sus sesiones se cierran ahora.`
            : `${toggling?.displayName ?? ''} podrá ingresar de nuevo con los mismos permisos.`}
        </p>
      </ReasonDialog>

      <PasswordResetDialog
        target={resetting?.name}
        mustChange={resetting?.mustChange === true}
        busy={busy}
        onConfirm={(password, reason) => {
          if (resetting === undefined) return;
          void run(() => backoffice.resetPassword(resetting.id, password, reason), 'Contraseña restablecida.').then(
            (ok) => {
              if (ok) setResetting(undefined);
            },
          );
        }}
        onClose={() => {
          setResetting(undefined);
        }}
      />
      <ReasonDialog
        open={resettingFactor !== undefined}
        title="¿Reiniciar la verificación en dos pasos?"
        confirmLabel="Reiniciar"
        danger
        busy={busy}
        onConfirm={(reason) => {
          if (resettingFactor === undefined) return;
          void run(
            () => backoffice.resetSecondFactor(resettingFactor.id, reason),
            'Verificación en dos pasos reiniciada.',
            [['staff-users']],
          ).then((ok) => {
            if (ok) setResettingFactor(undefined);
          });
        }}
        onClose={() => {
          setResettingFactor(undefined);
        }}
      >
        <p>
          Usalo solo si {resettingFactor?.displayName} perdió su teléfono y sus códigos de recuperación. Se cierran sus
          sesiones y, al ingresar con su contraseña, tendrá que configurar la verificación en dos pasos de nuevo. Nadie
          puede reiniciar la suya: lo hace otro administrador.
        </p>
      </ReasonDialog>
      <ReasonDialog
        open={ending !== undefined}
        title="¿Cerrar todas las sesiones?"
        confirmLabel="Cerrar sesiones"
        danger
        busy={busy}
        onConfirm={(reason) => {
          if (ending === undefined) return;
          void run(() => backoffice.revokeSessions(ending.id, reason), 'Sesiones cerradas.', [['staff-users']]).then(
            (ok) => {
              if (ok) setEnding(undefined);
            },
          );
        }}
        onClose={() => {
          setEnding(undefined);
        }}
      >
        <p>{ending?.displayName} tendrá que ingresar de nuevo en todos sus dispositivos.</p>
      </ReasonDialog>
    </>
  );
}

// ── Auditoría (TENANT_ADMIN, AUDITOR): actions, access events and slot changes (prompt11 §16) ──

const AREAS = [
  { value: '', label: 'Todo' },
  { value: 'identity', label: 'Usuarios, contraseñas y dos pasos' },
  { value: 'catalog', label: 'Grupos, colegios y hoteles' },
  { value: 'stays', label: 'Estadías y habitaciones' },
  { value: 'members', label: 'Integrantes y firmante' },
  { value: 'imports', label: 'Importaciones' },
  { value: 'rooming.distribution', label: 'Confirmación y reapertura' },
  { value: 'rooming.exchange-request', label: 'Solicitudes de intercambio' },
  { value: 'rooming.exchange', label: 'Intercambios hechos por un administrador' },
  { value: 'rooming.list', label: 'Descargas de la rooming list' },
  { value: 'rooming.roster', label: 'Consultas de documentos' },
];

/** What happened, in words. Unknown actions fall back to their technical name (never hidden). */
const ACTION_TEXT: Record<string, string> = {
  'identity.staff.created': 'Alta de personal',
  'identity.role.granted': 'Rol otorgado',
  'identity.role.revoked': 'Rol quitado',
  'identity.sessions.revoked': 'Sesiones cerradas',
  'identity.password.reset': 'Contraseña restablecida',
  'identity.mfa.reset': 'Verificación en dos pasos reiniciada',
  'identity.external.created': 'Alta de usuario de hotel o guía',
  'identity.external.granted': 'Permiso otorgado a hotel o guía',
  'identity.external.revoked': 'Permiso quitado a hotel o guía',
  'identity.external.status-changed': 'Cuenta de hotel o guía habilitada o deshabilitada',
  'identity.external.disabled': 'Cuenta de hotel o guía deshabilitada',
  'identity.external.enabled': 'Cuenta de hotel o guía habilitada',
  'catalog.school.created': 'Colegio creado',
  'catalog.school.updated': 'Colegio modificado',
  'catalog.contract.created': 'Contrato creado',
  'catalog.contract.updated': 'Contrato modificado',
  'catalog.hotel.created': 'Hotel creado',
  'catalog.hotel.updated': 'Hotel modificado',
  'catalog.group.created': 'Grupo creado',
  'catalog.group.updated': 'Grupo modificado',
  'catalog.group.exchanges-changed': 'Intercambios del grupo habilitados o deshabilitados',
  'stays.stay.created': 'Estadía creada',
  'stays.stay.updated': 'Estadía modificada',
  'stays.stay.opened': 'Selección abierta',
  'stays.stay.closed': 'Estadía cerrada',
  'stays.stay.hotel-message-set': 'Mensaje para el hotel guardado',
  'stays.stay.hotel-message-cleared': 'Mensaje para el hotel borrado',
  'stays.rooms.created': 'Habitaciones agregadas',
  'stays.room.updated': 'Habitación modificada',
  'stays.room.deleted': 'Habitación eliminada',
  'stays.room.physical-label-set': 'Número de habitación del hotel cargado',
  'members.member.updated': 'Datos de un integrante modificados',
  'members.member.cancelled': 'Integrante dado de baja',
  'members.passenger.created-by-guide': 'Pasajero agregado por un guía',
  'members.password.reset-by-guide': 'Acceso de un pasajero restablecido por un guía',
  'members.signer.designated': 'Firmante designado o reemplazado',
  'members.document.viewed': 'Documento de un integrante consultado',
  'imports.batch.committed': 'Importación confirmada',
  'imports.batch.rejected': 'Importación rechazada',
  'rooming.distribution.confirmed': 'Distribución confirmada',
  'rooming.distribution.reopened': 'Distribución reabierta',
  'rooming.exchange-request.requested': 'Solicitud de intercambio creada',
  'rooming.exchange-request.accepted': 'Solicitud de intercambio aceptada',
  'rooming.exchange-request.rejected': 'Solicitud de intercambio rechazada',
  'rooming.exchange-request.cancelled': 'Solicitud de intercambio cancelada',
  'rooming.exchange-request.expired': 'Solicitud de intercambio vencida',
  'rooming.exchange-request.invalidated': 'Solicitud de intercambio sin efecto',
  'rooming.exchange.performed-by-administrator': 'Intercambio hecho por un administrador',
  'rooming.list.exported': 'Rooming list descargada',
  'rooming.roster.documents-viewed': 'Documentos de los pasajeros consultados',
};

const ACCESS_TEXT: Record<string, string> = {
  LOGIN_PASSWORD_OK: 'Ingreso: contraseña correcta',
  LOGIN_FAILED: 'Ingreso fallido',
  LOGIN_WHILE_LOCKED: 'Intento con la cuenta bloqueada',
  ACCOUNT_LOCKED: 'Cuenta bloqueada por intentos',
  LOGIN_DENIED_DISABLED: 'Ingreso rechazado: cuenta no habilitada',
  MFA_ENROLLMENT_STARTED: 'Dos pasos: configuración iniciada',
  MFA_ENROLLED: 'Dos pasos: configurado',
  MFA_OK: 'Dos pasos: código correcto',
  MFA_RECOVERY_CODE_USED: 'Dos pasos: código de recuperación usado',
  MFA_FAILED: 'Dos pasos: código incorrecto',
  MFA_ATTEMPTS_EXCEEDED: 'Dos pasos: demasiados intentos',
  LOGOUT: 'Salida',
  PASSWORD_CHANGED: 'Contraseña cambiada por la persona',
  SESSIONS_REVOKED: 'Sesiones cerradas',
  CREDENTIAL_ISSUED: 'Cuenta creada con su credencial inicial',
  INITIAL_CREDENTIAL_REPLACED: 'Eligió su propia contraseña',
  PASSWORD_RESET_BY_ADMIN: 'Contraseña restablecida por un administrador',
  MFA_RESET_BY_ADMIN: 'Dos pasos reiniciado por un administrador',
};
const ACCOUNT_KIND: Record<AccessEvent['accountKind'], string> = {
  STAFF: 'Personal',
  PASSENGER: 'Pasajero',
  APODERADO: 'Firmante o apoderado',
  EXTERNAL: 'Hotel o guía',
};
const SLOT_ACTION: Record<string, string> = {
  RESERVE: 'Reservó',
  CHANGE: 'Cambió',
  RELEASE: 'Liberó',
  ADMIN_ASSIGN: 'Asignado por el personal',
  ADMIN_MOVE: 'Movido por el personal',
  ADMIN_RELEASE: 'Liberado por el personal',
  CANCEL_MEMBER: 'Baja del integrante',
  SWAP: 'Intercambio acordado',
  ADMIN_SWAP: 'Intercambio hecho por un administrador',
};

/** In this computer's time (the trail is stored in UTC). */
const when = (iso: string): string =>
  new Intl.DateTimeFormat('es', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));

// ── The audit trail in words (owner, 2026-10-07: "no se entiende nada") ──
// The trail stores identifiers and counts, never names or documents. The screen resolves what it can (groups, hotels,
// stays) from the lists the administrator already has, says the rest in words, and leaves out internal identifiers.

const WHO_TEXT: Record<string, string> = {
  HOTEL: 'el hotel',
  HOTEL_USER: 'el hotel',
  COORDINATOR: 'el guía',
  STAFF: 'un administrador',
  TENANT_ADMIN: 'un administrador',
  SIGNER: 'el firmante',
  APODERADO: 'el firmante',
};
const FIELD_TEXT: Record<string, string> = {
  checkInAt: 'entrada',
  checkOutAt: 'salida',
  hotelId: 'hotel',
  selectionOpensAt: 'apertura de la selección',
  selectionClosesAt: 'cierre de la selección',
  firstNames: 'nombres',
  lastNames: 'apellidos',
  sex: 'sexo',
  document: 'documento',
  email: 'correo',
  name: 'nombre',
  code: 'código',
  city: 'ciudad',
  timezone: 'zona horaria',
  contactEmails: 'correos del hotel',
  status: 'estado',
  contractId: 'contrato',
  contractNumber: 'número de contrato',
};

interface AuditNames {
  readonly groups: readonly Group[] | undefined;
  readonly hotels: readonly Hotel[] | undefined;
  readonly stays: readonly Stay[] | undefined;
}

const groupName = (names: AuditNames, id: unknown): string | undefined => names.groups?.find((g) => g.id === id)?.name;
const stayName = (names: AuditNames, id: string): string | undefined => {
  const stay = names.stays?.find((s) => s.id === id);
  if (stay === undefined) return undefined;
  const hotel = names.hotels?.find((h) => h.id === stay.hotelId)?.name ?? 'hotel';
  return `${groupName(names, stay.groupId) ?? 'Grupo'} en ${hotel}`;
};

/** "Sobre qué": the object of the action, by name when the screen knows it. */
function subjectText(e: AuditEvent, names: AuditNames): string {
  switch (e.entityType) {
    case 'stay':
      return `Estadía: ${stayName(names, e.entityId) ?? 'una estadía'}`;
    case 'travel_group':
    case 'group':
      return `Grupo: ${groupName(names, e.entityId) ?? 'un grupo'}`;
    case 'hotel':
      return `Hotel: ${names.hotels?.find((h) => h.id === e.entityId)?.name ?? 'un hotel'}`;
    case 'room':
      return 'Una habitación';
    case 'group_member':
      return 'Un integrante';
    case 'user_account':
      return 'Una cuenta de usuario';
    case 'import_batch':
      return 'Una importación';
    case 'swap_request':
      return 'Una solicitud de intercambio';
    case 'school':
      return 'Un colegio';
    case 'contract':
      return 'Un contrato';
    default:
      return '—';
  }
}

/** The details, one short phrase each; internal identifiers and versions are left out. */
function detailParts(e: AuditEvent, names: AuditNames): string[] {
  const d = e.details;
  const parts: string[] = [];
  const count = (key: string, one: string, many: string): void => {
    const value = d[key];
    if (typeof value === 'number') parts.push(`${String(value)} ${value === 1 ? one : many}`);
  };
  if (typeof d['as'] === 'string') parts.push(`Por ${WHO_TEXT[d['as']] ?? d['as']}`);
  if (d['by'] === 'HOTEL_USER') parts.push('Lo cargó el hotel');
  if (typeof d['groupId'] === 'string' && e.entityType !== 'travel_group') {
    parts.push(`Grupo ${groupName(names, d['groupId']) ?? ''}`.trim());
  }
  if (typeof d['stayId'] === 'string') parts.push(`Estadía ${stayName(names, d['stayId']) ?? ''}`.trim());
  if (typeof d['role'] === 'string') {
    const role = d['role'];
    const text: string =
      role === 'HOTEL_USER'
        ? 'hotel'
        : role === 'COORDINATOR'
          ? 'guía'
          : role in ROLE_TEXT
            ? ROLE_TEXT[role as StaffRole]
            : role;
    parts.push(`Rol: ${text}`);
  }
  if (typeof d['type'] === 'string') parts.push(d['type'] === 'HOTEL' ? 'Tipo: hotel' : 'Tipo: guía');
  if (typeof d['kind'] === 'string') {
    const kind = d['kind'];
    parts.push(`Cuenta: ${kind in ACCOUNT_KIND ? ACCOUNT_KIND[kind as AccessEvent['accountKind']] : kind}`);
  }
  count('rows', 'fila', 'filas');
  count('passengers', 'pasajero', 'pasajeros');
  count('rooms', 'habitación', 'habitaciones');
  count('created', 'creado', 'creados');
  count('updated', 'actualizado', 'actualizados');
  count('unchanged', 'sin cambios', 'sin cambios');
  count('accounts', 'cuenta creada', 'cuentas creadas');
  count('rowsWithErrors', 'fila con errores', 'filas con errores');
  count('length', 'carácter', 'caracteres');
  count('revokedSessions', 'sesión cerrada', 'sesiones cerradas');
  count('sessions', 'sesión cerrada', 'sesiones cerradas');
  if (typeof d['assigned'] === 'number' && typeof d['eligible'] === 'number') {
    parts.push(`${String(d['assigned'])} de ${String(d['eligible'])} pasajeros con plaza`);
  }
  if (d['documents'] === true) parts.push('con documentos');
  if (d['documents'] === false) parts.push('sin documentos');
  if (d['mustChange'] === true) parts.push('deberá cambiarla al ingresar');
  if (d['hadSecondFactor'] === true) parts.push('tenía la verificación configurada');
  if (d['missingSigner'] === true) parts.push('faltaba el firmante');
  if (d['signerDesignated'] === true) parts.push('con firmante');
  if (d['newPerson'] === true) parts.push('persona nueva');
  if (d['newPerson'] === false) parts.push('un integrante que ya estaba');
  if (d['credentialReissued'] === true) parts.push('con nueva contraseña inicial');
  if (d['source'] === 'cli') parts.push('por comando de operación');
  if (typeof d['from'] === 'boolean' && typeof d['to'] === 'boolean') {
    parts.push(d['to'] ? 'Quedaron habilitados' : 'Quedaron deshabilitados');
  } else if (typeof d['from'] === 'string') {
    parts.push(`Estado anterior: ${STAY_STATUS[d['from']]?.text ?? d['from']}`);
  }
  if (typeof d['fields'] === 'string' && d['fields'] !== '') {
    parts.push(
      `Cambió: ${d['fields']
        .split(',')
        .map((f) => FIELD_TEXT[f] ?? f)
        .join(', ')}`,
    );
  }
  return parts;
}
/** Start of a local day as an instant with offset (the API refuses a time without one). */
const dayStart = (day: string, plusDays = 0): string | undefined => {
  if (day === '') return undefined;
  const date = new Date(`${day}T00:00:00`);
  if (Number.isNaN(date.getTime())) return undefined;
  date.setDate(date.getDate() + plusDays);
  return date.toISOString();
};

const AUDIT_VIEWS = [
  ['actions', 'Acciones'],
  ['access', 'Accesos'],
  ['slots', 'Cambios de plaza'],
] as const;
type AuditView = (typeof AUDIT_VIEWS)[number][0];

export function AuditPage() {
  const [view, setView] = useState<AuditView>('actions');
  return (
    <>
      <PageTitle subtitle="Quién hizo qué, cuándo, sobre qué y por qué. No se puede editar ni borrar.">
        Auditoría
      </PageTitle>
      <div className="flex flex-col gap-4">
        <div role="tablist" aria-label="Vistas de la auditoría" className="flex flex-wrap gap-2">
          {AUDIT_VIEWS.map(([key, text]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={view === key}
              className={pillClass(view === key)}
              onClick={() => {
                setView(key);
              }}
            >
              {text}
            </button>
          ))}
        </div>
        {view === 'actions' ? <AuditActions /> : view === 'access' ? <AuditAccess /> : <AuditSlotChanges />}
      </div>
    </>
  );
}

/** Newer / older buttons of a cursor-paginated list (newest first). */
function Pager({
  cursors,
  next,
  onChange,
}: {
  readonly cursors: readonly string[];
  readonly next: string | null;
  readonly onChange: (cursors: string[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button
        variant="secondary"
        disabled={cursors.length === 0}
        onClick={() => {
          onChange(cursors.slice(0, -1));
        }}
      >
        Más recientes
      </Button>
      <Button
        variant="secondary"
        disabled={next === null}
        onClick={() => {
          if (next !== null) onChange([...cursors, next]);
        }}
      >
        Más antiguos
      </Button>
    </div>
  );
}

function AuditActions() {
  const groups = useQuery({ queryKey: ['groups'], queryFn: backoffice.groups });
  const hotels = useQuery({ queryKey: ['hotels'], queryFn: backoffice.hotels });
  const stays = useQuery({ queryKey: ['stays', 'all'], queryFn: () => backoffice.stays() });
  const names: AuditNames = { groups: groups.data, hotels: hotels.data, stays: stays.data };
  const [area, setArea] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors.at(-1);
  const range = { from: dayStart(from), to: dayStart(to, 1) };
  const events = useQuery({
    queryKey: ['audit', 'actions', area, from, to, cursor ?? ''],
    queryFn: () =>
      backoffice.auditEvents({
        ...(area === '' ? {} : { action: area }),
        ...(range.from === undefined ? {} : { from: range.from }),
        ...(range.to === undefined ? {} : { to: range.to }),
        ...(cursor === undefined ? {} : { cursor }),
      }),
  });
  return (
    <>
      <div className="grid gap-4 md:grid-cols-3">
        <SelectField
          label="Área"
          options={AREAS}
          value={area}
          onChange={(event) => {
            setArea(event.target.value);
            setCursors([]);
          }}
        />
        <TextField
          label="Desde el día"
          type="date"
          value={from}
          onChange={(event) => {
            setFrom(event.target.value);
            setCursors([]);
          }}
        />
        <TextField
          label="Hasta el día"
          type="date"
          value={to}
          onChange={(event) => {
            setTo(event.target.value);
            setCursors([]);
          }}
        />
      </div>
      <Loaded query={events}>
        {(page) => (
          <>
            <DataList<AuditEvent>
              caption="Acciones registradas"
              rows={page.items}
              rowKey={(e) => e.id}
              empty="No hay acciones para mostrar."
              columns={[
                { header: 'Cuándo', cell: (e) => when(e.occurredAt) },
                { header: 'Quién', cell: (e) => e.actorName ?? (e.actorUserId === null ? 'El sistema' : '—') },
                {
                  header: 'Qué hizo',
                  // The technical name stays available as a tooltip (for support), never as the text.
                  cell: (e) => <span title={e.action}>{ACTION_TEXT[e.action] ?? e.action}</span>,
                },
                { header: 'Sobre qué', cell: (e) => subjectText(e, names) },
                {
                  header: 'Detalle',
                  cell: (e) => {
                    const parts = detailParts(e, names);
                    return parts.length === 0 ? '—' : <span className="text-sm">{parts.join(' · ')}</span>;
                  },
                },
                { header: 'Motivo', cell: (e) => e.reason ?? '—' },
              ]}
            />
            <Pager cursors={cursors} next={page.nextCursor} onChange={setCursors} />
          </>
        )}
      </Loaded>
    </>
  );
}

function AuditAccess() {
  const [eventType, setEventType] = useState('');
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors.at(-1);
  const events = useQuery({
    queryKey: ['audit', 'access', eventType, cursor ?? ''],
    queryFn: () =>
      backoffice.accessEvents({
        ...(eventType === '' ? {} : { eventType }),
        ...(cursor === undefined ? {} : { cursor }),
      }),
  });
  return (
    <>
      <div className="max-w-md">
        <SelectField
          label="Tipo de evento"
          options={[
            { value: '', label: 'Todos' },
            ...Object.entries(ACCESS_TEXT).map(([value, label]) => ({ value, label })),
          ]}
          value={eventType}
          onChange={(event) => {
            setEventType(event.target.value);
            setCursors([]);
          }}
        />
      </div>
      <Loaded query={events}>
        {(page) => (
          <>
            <DataList<AccessEvent>
              caption="Accesos registrados"
              rows={page.items}
              rowKey={(e) => e.id}
              empty="No hay accesos para mostrar."
              columns={[
                { header: 'Cuándo', cell: (e) => when(e.occurredAt) },
                { header: 'Cuenta', cell: (e) => e.accountName },
                { header: 'Tipo de cuenta', cell: (e) => ACCOUNT_KIND[e.accountKind] },
                { header: 'Evento', cell: (e) => ACCESS_TEXT[e.eventType] ?? e.eventType },
              ]}
            />
            <Pager cursors={cursors} next={page.nextCursor} onChange={setCursors} />
          </>
        )}
      </Loaded>
    </>
  );
}

function AuditSlotChanges() {
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors.at(-1);
  const changes = useQuery({
    queryKey: ['audit', 'slots', cursor ?? ''],
    queryFn: () => backoffice.slotChanges(cursor === undefined ? {} : { cursor }),
  });
  return (
    <Loaded query={changes}>
      {(page) => (
        <>
          <DataList<HistoryEntry>
            caption="Cambios de plaza registrados"
            rows={page.items}
            rowKey={(e) => e.id}
            empty="Todavía no hubo cambios de plaza."
            columns={[
              { header: 'Cuándo', cell: (e) => when(e.occurredAt) },
              { header: 'Pasajero', cell: (e) => e.memberName },
              { header: 'Qué', cell: (e) => SLOT_ACTION[e.action] ?? e.action },
              { header: 'De → a', cell: (e) => `${e.fromRoom ?? '—'} → ${e.toRoom ?? '—'}` },
              { header: 'Quién', cell: (e) => e.actorName ?? '—' },
              { header: 'Motivo', cell: (e) => e.reason ?? '—' },
            ]}
          />
          <Pager cursors={cursors} next={page.nextCursor} onChange={setCursors} />
        </>
      )}
    </Loaded>
  );
}

/**
 * "Ayuda" (owner, 2026-10-07: the old "Configuración" had nothing to set, only texts). How the whole process works, in
 * the order of the menu, and the rules the system applies. Nothing here is a switch: what is configurable lives where
 * it belongs (exchanges are switched on or off in each group's file — prompt11 §15).
 */
export function HelpPage() {
  const steps: readonly [string, ReactNode][] = [
    [
      'Hoteles',
      <>
        Cargá cada <strong>hotel</strong> con su ciudad, su zona horaria y sus <strong>correos</strong> (ahí llegan la
        confirmación de la distribución y tus mensajes).
      </>,
    ],
    [
      'Grupos',
      <>
        Un <strong>grupo</strong> es el curso que viaja (por ejemplo «4.º medio A»). Al crearlo indicás el{' '}
        <strong>colegio</strong> (lo buscás, o lo escribís y apretás Enter para crearlo) y el{' '}
        <strong>código de contrato</strong> del ERP (si no existe, se crea solo). Después, en su ficha, pestaña{' '}
        <strong>Importaciones</strong>, subís el archivo con los pasajeros y el firmante: ahí se crean sus cuentas.
      </>,
    ],
    [
      'Estadía',
      <>
        Una <strong>estadía</strong> es ese grupo en <strong>un hotel</strong>, entre una fecha de entrada y una de
        salida. No es lo mismo que el grupo: un grupo puede tener más de una estadía (por ejemplo, dos hoteles en el
        mismo viaje). Se crea en la ficha del grupo, pestaña <strong>Resumen</strong>. Ahí también le dejás un{' '}
        <strong>mensaje al hotel</strong>.
      </>,
    ],
    [
      'Habitaciones',
      <>
        En la pestaña <strong>Habitaciones</strong> agregás las habitaciones de la estadía (Mujeres o Varones y cuántas
        camas). Después, en <strong>Resumen</strong>, tocás <strong>Abrir selección</strong>: desde ese momento los
        pasajeros eligen su cama.
      </>,
    ],
    [
      'Usuarios del hotel y del guía',
      <>
        En <strong>Usuarios</strong> creás la cuenta del hotel o del guía y le das acceso:{' '}
        <strong>Dar acceso a una estadía</strong> (hotel: ve esa estadía, carga los números reales de habitación y
        descarga la rooming list) o <strong>Dar acceso a un grupo</strong> (guía: ve las estadías de ese grupo).
      </>,
    ],
    [
      'Distribución y confirmación',
      <>
        En el <strong>Dashboard</strong> ves el avance de cada estadía. Cuando el 80 % de los pasajeros tiene cama, el{' '}
        <strong>firmante</strong> confirma la distribución: el hotel recibe un correo y todos una notificación.
      </>,
    ],
  ];
  return (
    <>
      <PageTitle subtitle="Cómo funciona el sistema, paso a paso, y las reglas que aplica.">Ayuda</PageTitle>
      <div className="flex flex-col gap-5">
        <Card className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">El proceso, en orden</h2>
          <ol className="flex flex-col gap-3">
            {steps.map(([title, text], index) => (
              <li key={title} className="flex gap-3">
                <span
                  aria-hidden="true"
                  className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-500 font-bold text-on-brand"
                >
                  {index + 1}
                </span>
                <div>
                  <h3 className="font-semibold">{title}</h3>
                  <p className="text-base">{text}</p>
                </div>
              </li>
            ))}
          </ol>
        </Card>
        <h2 className="text-xl font-semibold">Reglas</h2>
        <Card className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold">Intercambios entre pasajeros</h2>
          <p className="text-base">
            Se habilitan o deshabilitan <strong>por grupo</strong>, en la pestaña Resumen de cada grupo. Cada solicitud
            vence a las <strong>24 horas</strong>: una solicitud vencida no se puede aceptar ni se renueva; el pasajero
            puede enviar una nueva. Si un pasajero no puede ingresar, un administrador puede hacer el intercambio por él
            desde la pestaña Distribución, con un motivo.
          </p>
        </Card>
        <Card className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold">Firmante y confirmación de la distribución</h2>
          <p className="text-base">
            Cada grupo tiene siempre <strong>un firmante</strong>: viene en el archivo de importación (rol «firmante») o
            lo registra un administrador en la pestaña Integrantes. Confirma la distribución cuando el{' '}
            <strong>80 %</strong> de los pasajeros tiene plaza. Solo un administrador puede reabrir una distribución
            confirmada o cerrar la estadía después del viaje, con un motivo.
          </p>
        </Card>
        <Alert tone="info" title="Recuperación de contraseñas y de la verificación en dos pasos">
          Si alguien pierde su contraseña, un administrador le pone una provisoria: en Usuarios (personal, hoteles y
          guías) o en la pestaña Integrantes del grupo (pasajeros y firmante). Si un administrador pierde su segundo
          factor, otro administrador lo reinicia en Usuarios. No hay recuperación por correo.
        </Alert>
      </div>
    </>
  );
}
